import React from 'react';
import PropTypes from 'prop-types';
import { dynastyTheme, withAlpha } from '../../../../../services/colorService';

const C = dynastyTheme.tokens.colors;

/**
 * StatRow Component
 * 
 * Single-line stat display with optional trend indicator and rank.
 * Used for simple stat displays, info cards, and comparisons.
 * 
 * @param {string} label - Stat label (e.g., "AVG", "Home Runs")
 * @param {string|number} value - Stat value
 * @param {string} trend - Optional trend indicator: "up" | "down" | null
 * @param {number} rank - Optional rank number
 * @param {string} size - Display size: "small" | "medium" | "large"
 */
const StatRow = ({ 
  label, 
  value, 
  trend = null, 
  rank = null, 
  size = 'medium' 
}) => {
  // Size configurations
  const sizeConfig = {
    small: {
      labelSize: '11px',
      valueSize: '14px',
      height: '32px',
      gap: '8px'
    },
    medium: {
      labelSize: '12px',
      valueSize: '16px',
      height: '40px',
      gap: '12px'
    },
    large: {
      labelSize: '14px',
      valueSize: '18px',
      height: '48px',
      gap: '16px'
    }
  };

  const sizeStyles = sizeConfig[size] || sizeConfig.medium;

  // Trend arrow
  const getTrendArrow = () => {
    if (trend === 'up') return '↗️';
    if (trend === 'down') return '↘️';
    return null;
  };

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: sizeStyles.height,
    padding: '0 12px',
    backgroundColor: C.surface[855], // dynastyTheme.colors.darkLighter
    borderRadius: '6px',
    gap: sizeStyles.gap
  };

  const labelStyle = {
    fontSize: sizeStyles.labelSize,
    fontWeight: '500',
    color: C.coolGray[400], // dynastyTheme.colors.gray
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    flex: '0 0 auto'
  };

  const rightSideStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: '0 0 auto'
  };

  const valueStyle = {
    fontSize: sizeStyles.valueSize,
    fontWeight: '600',
    color: C.white,
    whiteSpace: 'nowrap'
  };

  const trendStyle = {
    fontSize: '14px',
    lineHeight: 1
  };

  const rankStyle = {
    fontSize: '12px',
    fontWeight: '600',
    color: C.gold.bright, // dynastyTheme.colors.primary
    backgroundColor: withAlpha(C.gold.bright, 0.15),
    padding: '2px 6px',
    borderRadius: '4px',
    whiteSpace: 'nowrap'
  };

  return (
    <div style={containerStyle}>
      <span style={labelStyle}>{label}</span>
      <div style={rightSideStyle}>
        <span style={valueStyle}>{value}</span>
        {getTrendArrow() && (
          <span style={trendStyle}>{getTrendArrow()}</span>
        )}
        {rank !== null && (
          <span style={rankStyle}>#{rank}</span>
        )}
      </div>
    </div>
  );
};

StatRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  trend: PropTypes.oneOf(['up', 'down', null]),
  rank: PropTypes.number,
  size: PropTypes.oneOf(['small', 'medium', 'large'])
};

export default StatRow;
